import type { SetStateAction } from 'react';
import { useCallback, useState } from 'react';
import { useStore, useStoreApi } from '@/app/providers/store';
import type { FormImagePrompt, ImageUploadFile, MultiFrameTransition } from '@/app/types';
import { useImagePrompts } from './use-image-prompts';
import { useTransitionSync } from './use-transition-sync';

export const useImg2VideoForm = (options?: {
  maxCount?: number;
  onImagePromptsChange?: () => void;
  allowTempMode?: boolean;
}) => {
  const { setFormParams, multiFrameTransitions } = useStore((state) => ({
    setFormParams: state.setFormParams,
    multiFrameTransitions: state.img2videoFormParams.multiFrameTransitions,
  }));
  const store = useStoreApi();

  const [currentTransitionIndex, setCurrentTransitionIndex] = useState<number>(0);

  const getImg2VideoFormParams = useCallback(() => {
    return store.getState().img2videoFormParams;
  }, [store]);

  const {
    imagePrompts,
    validImageCount,
    handleUploadImage: baseHandleUploadImage,
    handleRemoveImage: baseHandleRemoveImage,
    handleImageChange,
    handleSwapImages,
    clearImages,
    setImages,
  } = useImagePrompts({
    formType: 'img2video',
    maxCount: options?.maxCount,
    onImagePromptsChange: options?.onImagePromptsChange,
    allowTempMode: options?.allowTempMode,
  });

  const {
    syncTransitionsAfterImageCountChange: baseSyncTransitionsAfterImageCountChange,
    handleRemoveImageTransition: baseHandleRemoveImageTransition,
  } = useTransitionSync();

  const updateTransitionDuration = useCallback(
    (index: number, duration: number) => {
      if (!multiFrameTransitions || !multiFrameTransitions[index]) return;

      const newTransitions = [...multiFrameTransitions];
      newTransitions[index] = {
        ...newTransitions[index],
        duration,
      };
      setFormParams('img2video', { multiFrameTransitions: newTransitions });
    },
    [multiFrameTransitions, setFormParams],
  );

  const updateTransitionPrompt = useCallback(
    (index: number, prompt: string) => {
      if (!multiFrameTransitions || !multiFrameTransitions[index]) {
        return;
      }

      const newTransitions = [...multiFrameTransitions];
      newTransitions[index] = {
        ...newTransitions[index],
        prompt: prompt || undefined,
      };
      setFormParams('img2video', { multiFrameTransitions: newTransitions });
    },
    [multiFrameTransitions, setFormParams],
  );

  const handleRemoveImageTransition = useCallback(
    (currentTransitions: MultiFrameTransition[], removedIndex: number) => {
      baseHandleRemoveImageTransition(currentTransitions, removedIndex, () => {
        const formParams = getImg2VideoFormParams();
        const adjusted = formParams.multiFrameTransitions || [];
        const newLength = adjusted.length;
        if (newLength === 0) {
          setCurrentTransitionIndex(0);
        } else if (currentTransitionIndex >= newLength) {
          setCurrentTransitionIndex(newLength - 1);
        } else if (newLength === 1) {
          setCurrentTransitionIndex(0);
        }
      });
    },
    [baseHandleRemoveImageTransition, getImg2VideoFormParams, setCurrentTransitionIndex, currentTransitionIndex],
  );

  const syncTransitionsAfterImageCountChange = useCallback(
    (newValidImageCount: number, removedIndex?: number) => {
      if (removedIndex !== undefined) {
        const formParams = getImg2VideoFormParams();
        const currentTransitions = formParams.multiFrameTransitions || [];
        handleRemoveImageTransition(currentTransitions, removedIndex);
        return;
      }

      baseSyncTransitionsAfterImageCountChange(newValidImageCount);
    },
    [getImg2VideoFormParams, handleRemoveImageTransition, baseSyncTransitionsAfterImageCountChange],
  );

  const handleUploadImage = useCallback(
    (uploadFile: ImageUploadFile) => {
      baseHandleUploadImage(uploadFile);
      if (uploadFile.status === 'done') {
        const currentImagePrompts = getImg2VideoFormParams().imagePrompts.filter((p) => p !== null);
        const newValidImageCount = currentImagePrompts.length;
        syncTransitionsAfterImageCountChange(newValidImageCount);
      }
    },
    [baseHandleUploadImage, getImg2VideoFormParams, syncTransitionsAfterImageCountChange],
  );

  const handleRemoveImage = useCallback(
    (idx: number) => {
      const currentValid = getImg2VideoFormParams().imagePrompts.filter((p) => p !== null).length;
      const newValidImageCount = Math.max(0, currentValid - 1);
      baseHandleRemoveImage(idx);
      syncTransitionsAfterImageCountChange(newValidImageCount, idx);
    },
    [baseHandleRemoveImage, getImg2VideoFormParams, syncTransitionsAfterImageCountChange],
  );

  const wrappedSetImages = useCallback(
    (updater: SetStateAction<FormImagePrompt[]>) => {
      setImages(updater);
      setTimeout(() => {
        const currentImagePrompts = getImg2VideoFormParams().imagePrompts.filter((p) => p !== null);
        const newValidImageCount = currentImagePrompts.length;
        syncTransitionsAfterImageCountChange(newValidImageCount);
      }, 0);
    },
    [setImages, getImg2VideoFormParams, syncTransitionsAfterImageCountChange],
  );

  return {
    imagePrompts,
    validImageCount,
    handleUploadImage,
    handleRemoveImage,
    handleImageChange,
    handleSwapImages,
    clearImages,
    setImages: wrappedSetImages,
    currentTransitionIndex,
    setCurrentTransitionIndex,
    multiFrameTransitions,
    updateTransitionDuration,
    updateTransitionPrompt,
    syncTransitionsAfterImageCountChange,
  };
};
